/**
 * Common ProductReviews component used by other modules as a reusable component which returns the ui for a product reviews.
 *
 * @module views/Organisms/ProductReviews
 * @memberof -Common
 */
import './ProductReviews.scss';

import React, { useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import PropTypes from 'prop-types';

import JSscriptTag from '@ulta/core/components/JSscriptTag/JSscriptTag';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { constants } from '@ulta/core/utils/constants/constants';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';
import {
  formatLocale,
  getApplicationLocale
} from '@ulta/core/utils/locale/locale';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';
import { host } from '@ulta/utils/Formatters/Formatters';

import * as utils from './ProductReviews';

/**
 * Represents a ProductReviews component
 *
 * @method
 * @param {ProductReviewsProps} props - React properties passed from composition
 * @returns ProductReviews
 */
const ProductReviews = ( props ) => {
  const {
    prApiKey,
    prMerchantGroupId,
    prMerchantId,
    locale,
    questionsTitle,
    questionsAction,
    reviewsTitle,
    reviewsAction,
    stockStatus,
    rating,
    reviewCount,
    description,
    brandName,
    productName,
    variantType,
    variantName,
    imageUrl,
    productId,
    productSku,
    price,
    priceCurrency,
    productURL,
    questionsEnabled,
    enableLocalPowerReviewContent,
    rootMargin = defaultProps.rootMargin,
    root = defaultProps.root,
    threshold = defaultProps.threshold,
    reviewSEOtitle,
    ratingLabel,
    reviewsLabel,
    positiveReviewLabel,
    criticalReviewLabel,
    reviewedByLabel,
    reviewerNameByLocationLabel,
    positiveHeadline,
    positiveComments,
    negativeHeadline,
    negativeComments,
    reviews
  } = props;

  const { powerReviewsSdk } = useAppConfigContext();
  const userLocale = formatLocale( getApplicationLocale( locale, isServer() ) );

  let reviewsObj = '';
  if( reviews?.length > 0 ){
    reviewsObj = {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating,
        reviewCount: reviewCount
      },
      review: []
    };
    let reviewItem;
    reviews.map( ( item ) => {
      reviewItem = {
        '@type': 'Review',
        name: item.headline,
        reviewBody: item.comment,
        datePublished: item.createdDate,
        author: {
          '@type': 'Person',
          name: item.nickname
        },
        locationCreated: {
          '@type': 'AdministrativeArea',
          name: item.location
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: item.rating
        }
      };
      reviewsObj.review.push( reviewItem );
    } );
  }

  const schemaProductName = variantName ?
    `${productName} - ${variantName}` :
    productName;

  const productObj = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    description: description,
    brand: brandName,
    name: schemaProductName,
    image: imageUrl,
    productID: productId,
    sku: productSku,
    offers: {
      '@type': 'Offer',
      availability: `https://schema.org/${stockStatus}`,
      price: price,
      priceCurrency: priceCurrency,
      url: productURL
    }
  };
  if( variantType ){
    productObj[variantType] = variantName;
  }

  const prodReviewsObj = {
    ...productObj,
    ...( enableLocalPowerReviewContent && reviewsObj )
  };
  const productReviewsRef = useRef();
  const [currentProductReviewsRef, setCurrentProductReviewsRef] = useState( '' );
  const { hasIntersected } = useIntersectionObserver(
    productReviewsRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );

  useEffect( () => {
    // set productReviewsRef
    if( productReviewsRef.current ){
      setCurrentProductReviewsRef( productReviewsRef.current );
    }
  }, [] );

  return (
    <section className='ProductReviews' ref={ productReviewsRef }>
      <div className='ProductReviews__Content'>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={ { __html: JSON.stringify( prodReviewsObj ) } }
        ></script>
        { hasIntersected && powerReviewsSdk && (
          <JSscriptTag
            tagId='powerReviewsUI'
            src={ powerReviewsSdk }
            async={ true }
            immediate={ true }
          />
        ) }
        { hasIntersected && enableLocalPowerReviewContent && (
          <>
            <JSscriptTag
              tagId='PowerReviewsRender'
              body={ utils.composePowerReviewsRender( {
                prApiKey,
                prMerchantGroupId,
                prMerchantId,
                host,
                userLocale,
                productId
              } ) }
              immediate={ true }
            />
            {
              <noscript>
                <>
                  <Text htmlTag='h3' textStyle='title-6'>
                    { reviewSEOtitle }
                  </Text>
                  <Text>{ ratingLabel }</Text>
                  <Text>{ reviewsLabel }</Text>
                  <Text htmlTag='h3' textStyle='title-6'>
                    { positiveReviewLabel }
                  </Text>
                  <Text htmlTag='h3' textStyle='title-6'>
                    { positiveHeadline }
                  </Text>
                  <Text>{ positiveComments }</Text>
                  <Text htmlTag='h3' textStyle='title-6'>
                    { criticalReviewLabel }
                  </Text>
                  <Text htmlTag='h3' textStyle='title-6'>
                    { negativeHeadline }
                  </Text>
                  <Text>{ negativeComments }</Text>
                  <Text htmlTag='h3' textStyle='title-6'>
                    { reviewedByLabel }
                  </Text>
                  { reviews.map( ( item, index ) => {
                    return (
                      <div key={ 'review' + index }>
                        <Text htmlTag='h3' textStyle='title-6'>
                          { item.headline }
                        </Text>
                        <Text>{ item.rating }</Text>
                        <Text>
                          { carouselUtils.handleTokenizedString(
                            reviewerNameByLocationLabel,
                            [item.nickname, item.location]
                          ) }
                        </Text>
                        <Text>{ item.createdDate }</Text>
                        <Text>{ item.comment }</Text>
                      </div>
                    );
                  } ) }
                </>
              </noscript>
            }
          </>
        ) }
        <div id='productQA' className='ProductReviews__QA--wrapper'>
          { questionsEnabled && (
            <>
              <div className='ProductReviews__QA--heading'>
                <Text htmlTag='h2' textStyle='title-6'>
                  { questionsTitle }
                </Text>
                <Link_Huge likeButtonOutline action={ questionsAction }>
                  { questionsAction.label }
                </Link_Huge>
              </div>
              <div
                id='qaDisplay'
                className='ProductReviews__QA--container'
              ></div>
            </>
          ) }
        </div>
        <div id='reviews' className='ProductReviews__Reviews--wrapper'>
          <div
            id='reviewsImages'
            className='ProductReviews__Reviews--containerImages'
          ></div>
          <div className='ProductReviews__Reviews--heading'>
            <Text htmlTag='h2' textStyle='title-6'>
              { reviewsTitle }
            </Text>
            <Link_Huge likeButtonOutline action={ reviewsAction }>
              { reviewsAction?.label }
            </Link_Huge>
          </div>
          <div
            id='reviewsSnap'
            className='ProductReviews__Reviews--containerSnap'
          ></div>
          <div
            id='reviewsList'
            className='ProductReviews__Reviews--containerList'
          ></div>
        </div>
      </div>
    </section>
  );
};

/**
 * composePowerReviewsRender helper function to render the script body
 *
 * @param {object} data - Arguments
 * @param {string} data.prApiKey - Sets the product review API key
 * @param {string} data.prMerchantGroupId - Sets the merchant group id .
 * @param {string} data.prMerchantId - Sets the merchant id.
 * @param {string} data.useLocale - Sets the locale.
 * @param {string} data.productId - Sets the product ID.
 * @param {string} data.host - Sets the host address
 */

export const composePowerReviewsRender = ( data ) => {
  const {
    prApiKey,
    prMerchantGroupId,
    prMerchantId,
    host,
    userLocale,
    productId
  } = handleEmptyObjects( data );

  return `var prUILoaded = setInterval( function() {
    if( typeof POWERREVIEWS !== 'undefined' ) {
      clearInterval( prUILoaded );
      POWERREVIEWS.display.render({
        ENABLE_CLIENT_SIDE_STRUCTURED_DATA: false,
        api_key: '${prApiKey}',
        locale: '${userLocale}',
        merchant_group_id: '${prMerchantGroupId}',
        merchant_id: '${prMerchantId}',
        page_id: '${productId}',
        review_wrapper_url: '//${host}/ulta/review/?pr_page_id=${productId}&pr_source=web',
        components: {
          ReviewImageDisplay: 'reviewsImages',
          ReviewSnapshot: 'reviewsSnap',
          ReviewList: 'reviewsList',
          QuestionDisplay: 'qaDisplay'
        }
      })
    }
  }, 100 );`;
};

/**
 * Property type definitions
 * @typedef ProductReviewsProps
 * @type {object}
 * @property {string} prApiKey - Sets the product review API key
 * @property {string} prMerchantGroupId - Sets the merchant group id .
 * @property {string} prMerchantId - Sets the merchant id.
 * @property {string} locale - Sets the locale.
 * @property {boolean} questionsEnabled - Sets wether to enable ask question or not.
 * @property {string} questionsTitle - Sets the question label.
 * @property {object} questionsAction - Sets the actions on question.
 * @property {string} reviewsTitle - Sets the review label.
 * @property {object} reviewsAction - Sets the review actions.
 * @property {string} stockStatus - Sets the product stock status.
 * @property {number} rating - Sets the review rating value.
 * @property {number} reviewCount - Sets the review rating count.
 * @property {string} description - Sets the product description.
 * @property {string} brandName - Sets the product brand name.
 * @property {string} productName - Sets the product name.
 * @property {string} variantType - Sets the product variant type.
 * @property {string} variantName - Sets the product variant name.
 * @property {string} imageUrl - Sets the product image URL.
 * @property {string} productId - Sets the product ID.
 * @property {string} productSku - Sets the product SKU.
 * @property {string} price - Sets the product price.
 * @property {string} priceCurrency - Sets the product priceCurrency.
 * @property {string} productURL - Sets the product URL.
 * @property {boolean} enableLocalPowerReviewContent - global switch to enable/disable PR content.
 * @property {string} reviewSEOtitle - Sets the noscript block title.
 * @property {string} ratingLabel - Sets the noscript block header product rating.
 * @property {string} reviewsLabel - Sets the noscript block header product reviews number.
 * @property {string} positiveReviewLabel - Sets the noscript block most helpful positive heading.
 * @property {string} criticalReviewLabel - Sets the noscript block most helpful critical heading.
 * @property {string} reviewedByLabel - Sets the noscript block reviewed by number heading.
 * @property {string} reviewerNameByLocationLabel - Sets the reviewer by location tokenized string.
 * @property {string} positiveHeadline - Sets the noscript block most helpful positive review headline.
 * @property {string} positiveComments - Sets the noscript block most helpful positive review comments.
 * @property {string} negativeHeadline - Sets the noscript block most helpful critical review headline.
 * @property {string} negativeComments - Sets the noscript block most helpful critical review comments.
 * @property {array} reviews - Sets the array of product reviews.
 */
export const propTypes = {
  /**
   * The prop that holds the API key.
   */
  prApiKey: PropTypes.string,
  /**
   * The prop that holds the merchant group id.
   */
  prMerchantGroupId: PropTypes.string,
  /**
   * The prop that holds the merchant id.
   */
  prMerchantId: PropTypes.string,
  /**
   * The prop that holds locale.
   */
  locale: PropTypes.string,
  /**
   * The prop that decides to enable asking question or not.
   */
  questionsEnabled: PropTypes.bool,
  /**
   * The prop that holds Q&A title.
   */
  questionsTitle: PropTypes.string,
  /**
   * The prop that holds actions on questions.
   */
  questionsAction: PropTypes.object,
  /**
   * The prop holds the label for reviews.
   */
  reviewsTitle: PropTypes.string,
  /**
   * The prop holds the actions on reviews.
   */
  reviewsAction: PropTypes.object,
  /**
   * The prop holds the stock status.
   */
  stockStatus: PropTypes.string,
  /**
   * The prop holds the rating value.
   */
  rating: PropTypes.number,
  /**
   * The prop holds the rating count.
   */
  reviewCount: PropTypes.number,
  /**
   * The prop holds the product description.
   */
  description: PropTypes.string,
  /**
   * The prop holds the product brand name.
   */
  brandName: PropTypes.string,
  /**
   * The prop holds the product name.
   */
  productName: PropTypes.string,
  /**
   * The prop holds the product variant type.
   */
  variantType: PropTypes.string,
  /**
   * The prop holds the product variant name.
   */
  variantName: PropTypes.string,
  /**
   * The prop holds the product image URL.
   */
  imageUrl: PropTypes.string,
  /**
   * The prop holds the product ID.
   */
  productId: PropTypes.string,
  /**
   * The prop holds the product SKU.
   */
  productSku: PropTypes.string,
  /**
   * The prop holds the product price.
   */
  price: PropTypes.string,
  /**
   * The prop holds the product price currency type.
   */
  priceCurrency: PropTypes.string,
  /**
   * The prop holds the product URL.
   */
  productURL: PropTypes.string,
  /**
   * The prop that decides to enable/disable content.
   */
  enableLocalPowerReviewContent: PropTypes.bool,
  /**
   * The prop for the noscript block title.
   */
  reviewSEOtitle: PropTypes.string,
  /**
   * The prop holds the noscript block header product rating.
   */
  ratingLabel: PropTypes.string,
  /**
   * The prop holds the noscript block header product reviews number.
   */
  reviewsLabel: PropTypes.string,
  /**
   * The prop holds the noscript block most helpful positive heading.
   */
  positiveReviewLabel: PropTypes.string,
  /**
   * The prop holds the noscript block most helpful critical heading.
   */
  criticalReviewLabel: PropTypes.string,
  /**
   * The prop holds the noscript block reviewed by number heading.
   */
  reviewedByLabel: PropTypes.string,
  /**
   * The prop holds the noscript block reviewer by location tokenized string.
   */
  reviewerNameByLocationLabel: PropTypes.string,
  /**
   * The prop holds the noscript block helpful positive review headline.
   */
  positiveHeadline: PropTypes.string,
  /**
   * The prop holds the noscript block helpful positive review comments.
   */
  positiveComments: PropTypes.string,
  /**
   * The prop holds the noscript block helpful critical review headline.
   */
  negativeHeadline: PropTypes.string,
  /**
   * The prop holds the noscript block helpful critical review comments.
   */
  negativeComments: PropTypes.string,
  /**
   * The prop holds the array of reviews.
   */
  reviews: PropTypes.array
};

export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ProductReviews.propTypes = propTypes;
ProductReviews.defaultProps = defaultProps;

export default ProductReviews;
